import gql from "graphql-tag";
import { useEffect, useRef, useState } from "react";

import { AnimatePresence } from "motion/react";
import { getLinkData } from "@/core/utils";
import { Button, ClientPortal, Container, CursorFollower, Image, RevealWrapper } from "@components";
import { InducteeArtifactPaperFragment } from "@graphqlTypes";
import ArtifactPaperModal from "./ArtifactPaperModal";

import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';
import { useInView, motion } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';

import styles from "./ArtifactPaper.module.scss";
import className from "classnames/bind";
import { usePageWrapperData } from "@/providers/PageWrapperContext";
const cx = className.bind(styles);

function getVisibleImages(images: any[], currentIndex: number): any[] {
  const visible = [];
  for (let i=0; i<3; i++) {
    const index = (currentIndex + i) % images.length;
    visible.push(images[index]);
  }
  return visible;
}

export default function ArtifactPaper(props: InducteeArtifactPaperFragment) {
  const {
    eyebrow,
    images,
    artifactPaperHeadline,
    artifactPaperBody,
    mediaRight,
    cta
  } = props;
  const imageWrapperEl = useRef<HTMLDivElement>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [cursorFollowerPos, setCursorFollowerPos] = useState<{x: number, y: number} | null>(null);
  const [showCursorFollower, setShowCursorFollower] = useState(false);
  const [modalActive, setModalActive] = useState(false);
  const layoutDirection: 'left' | 'right' = mediaRight ? 'right' : 'left';
  const ctaData = getLinkData(cta);
  const visibleImages = getVisibleImages(images, activeImage);
  const { labels } = usePageWrapperData();
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();

  // -------------------- Animation

  const rootEl = useRef<HTMLElement>(null);
  const isDesktopRef = useRef(isDesktop);
  const REVEAL = AnimVals;
  // Only use isDesktop when isReady to avoid SSR/client mismatch
  const triggerPercent = (mounted && isReady && isDesktop) ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });

  // -------------------- CursorFollower's Position

  const updateCursorFollowerPosition = () => {
    if (imageWrapperEl.current) {
      const rect = imageWrapperEl.current.getBoundingClientRect();
      let posX, posY;
      if (isDesktopRef.current) {
        if (layoutDirection ==='right') {
          posX = rect.width;
          posY = rect.height * 0.65;
        } else {
          posX = 0;
          posY = rect.height * 0.65;
        }
      } else {
        posX = rect.width - 36;
        posY = rect.height * 0.5;
      }
      setCursorFollowerPos({ x: posX, y: posY });
    }
  };
  useEffect(() => {
    isDesktopRef.current = isDesktop;
    updateCursorFollowerPosition();
  }, [isDesktop, isInView]);
  useEffect(() => {
    window.addEventListener('resize', updateCursorFollowerPosition);
    return () => {
      window.removeEventListener('resize', updateCursorFollowerPosition);
    };
  }, [])

  // -------------------- CursorFollower visibility control

  useEffect(() => {
    if (mounted && isReady && isDesktop && isInView && !modalActive) {
      setShowCursorFollower(true);
    } else {
      setShowCursorFollower(false);
    }
  }, [mounted, isReady, isDesktop, isInView, modalActive]);

  // -------------------- Render

  return (
    <section className={cx(["artifact-paper", `media-${layoutDirection}`])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
      <Container className={cx("content")}>
        <div className={cx("content__imagery")}>
          <div className={cx("image-wrapper")}
            onClick={() => setModalActive(true)}
            ref={imageWrapperEl}
          >
            {visibleImages.map(({ image, caption }, index) => (
              <div
                key={index}
                className={cx("image-container")}
                style={{ zIndex: visibleImages.length - index }}
              >
                <Image image={image} className={cx("image")} />
              </div>
            ))}
            {mounted && isReady && isDesktop && (
              <CursorFollower
                container={imageWrapperEl}
                circleText={labels?.zoomInLabel}
                externalPos={cursorFollowerPos}
                isVisible={showCursorFollower}
                mouseActive={showCursorFollower}
              />
            )}
          </div>
        </div>
        <div className={cx("content__text")}>
          <div className={cx("title")}>
            <p className={cx(["eyebrow-l", "eyebrow"])}>{eyebrow}</p>
            <h2 className={cx("title-uc-l", "title__lines")}>
              {artifactPaperHeadline.map((line, index) => (
                <span
                  key={index}
                  className={cx(["line", `title__lines__line-${index}`])}
                  dangerouslySetInnerHTML={{ __html: line.line }}
                />
              ))}
            </h2>
          </div>
          {
            artifactPaperBody?.length > 0 && (
              <div className={cx("body")}>
                {artifactPaperBody.map((line, index) => (
                  <div className={cx("body__p-container")} key={index}>
                    <p className={"text-m"}>{line.text}</p>
                    { index === artifactPaperBody.length - 1 && ctaData && (
                        <Button
                          label={ctaData.label}
                          className={cx("link")}
                          url={ctaData.url}
                          target={ctaData.target}
                        />
                      )}
                  </div>
                ))}
              </div>
            )
          }
        </div>
      </Container>
      </RevealWrapper>
      <ClientPortal selector="portal" show={modalActive}>
        <AnimatePresence>
          {modalActive && (
            <ArtifactPaperModal
              initial={{ opacity: 0, filter: "blur(10px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, filter: "blur(10px)" }}
              images={images}
              onClose={() => setModalActive(false)}
              onSlideChange={setActiveImage}
              startIndex={activeImage}
              key="artifact-paper-modal"
            />
          )}
        </AnimatePresence>
      </ClientPortal>
    </section>
  );

}

ArtifactPaper.fragments = {
  entry: gql`
    fragment InducteeArtifactPaper on InducteeDataImpactFlexibleContentArtifactPaperLayout {
      eyebrow
      mediaRight: alignRight
      cta {
        linkType
        label
        externalLink
        page {
          node {
            uri
          }
        }
      }
      images {
        image {
          ...ACFMedia
        }
        caption
      }
      artifactPaperHeadline {
        line
      }
      artifactPaperBody {
        text
      }
    }
  `,
};
